<script>
import ErrPage from '@src/views/Error';
import MainLayout from '@src/layouts/Main';
import { createVueMetaBranding, minifyCode } from '@src/lib/branding';
import { mapRoot } from '@/mixins/root';

export default {
  render(el) {
    const { company, fullPageErr } = this.$root;
    const { matched: [ parent ] } = this.$route;
    const { id: companyID } = company || {};

    if (fullPageErr) {
      return el(MainLayout, {}, [ el(ErrPage, { props: { err: fullPageErr } }) ]);
    } else if (parent && parent.components && parent.components.layout) {
      return el('router-view', {
        props: { name: 'layout' },
        id: 'app',
        scopedSlots: { default: props => el('router-view', { props: { companyID, ...props } }) },
      });
    } else {
      return el('router-view', { props: { companyID }, id: 'app' });
    }
  },
  computed: mapRoot([ 'isNativeApp' ]),
  metaInfo() {
    const { company } = this.$root;
    const isAdminUI = process.env.VUE_ENV === 'server' ? false : process.env.ADMIN_UI;
    const nonce = process.env.VUE_ENV === 'server' ? (this.$ssrContext.nonce || '') : '';

    const head = {};

    if (process.env.GOOGLE_TAG_MANAGER_ID) {
      head.script = (Array.isArray(head.script) ? head.script : []).concat({
        vmid: 'googleTagManagerPlatform',
        type: 'text/javascript',
        innerHTML: minifyCode(`
          (function(w,d,s,l,i){
            w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});
            var f=d.getElementsByTagName(s)[0],
            j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;
            ${nonce ? `j.setAttribute('nonce', '${nonce}');` : ''}
            j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;
            f.parentNode.insertBefore(j,f);
          })(window,document,'script','dataLayer','${process.env.GOOGLE_TAG_MANAGER_ID}');
        `),
        nonce,
      });
      head.noscript = (Array.isArray(head.noscript) ? head.noscript : []).concat({
        vmid: 'googleTagManagerNoscriptPlatform',
        innerHTML: minifyCode(`
          <iframe src="https://www.googletagmanager.com/ns.html?id=${process.env.GOOGLE_TAG_MANAGER_ID}"
          height="0" width="0" style="display:none;visibility:hidden"></iframe>
        `),
        pbody: true,
        nonce,
      });
      head.__dangerouslyDisableSanitizersByTagID = {
        ...head.__dangerouslyDisableSanitizersByTagID,
        googleTagManagerPlatform: [ 'innerHTML' ],
        googleTagManagerNoscriptPlatform: [ 'innerHTML' ],
      };
    }

    if (company && company.id) {
      const { googleTagManagerID, siteBranding: branding } = company;
      const companyName = this.$t('variables.company-name');
      head.titleTemplate = chunk => [].concat(chunk && typeof chunk === 'string' ? chunk : [], companyName).join(' - ');

      if (process.env.NODE_ENV === 'production' && googleTagManagerID) {
        head.script = (Array.isArray(head.script) ? head.script : []).concat({
          vmid: 'googleTagManagerCustom',
          type: 'text/javascript',
          innerHTML: minifyCode(`
            (function(w,d,s,l,i){
              w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});
              var f=d.getElementsByTagName(s)[0],
              j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;
              ${nonce ? `j.setAttribute('nonce', '${nonce}')` : ''}
              j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;
              f.parentNode.insertBefore(j,f);
            })(window,document,'script','dataLayer','${googleTagManagerID}');
          `),
          nonce,
        });
        head.noscript = (Array.isArray(head.noscript) ? head.noscript : []).concat({
          vmid: 'googleTagManagerNoscriptCustom',
          innerHTML: minifyCode(`
            <iframe src="https://www.googletagmanager.com/ns.html?id=${googleTagManagerID}"
            height="0" width="0" style="display:none;visibility:hidden"></iframe>
          `),
          pbody: true,
          nonce,
        });
        head.__dangerouslyDisableSanitizersByTagID = {
          ...head.__dangerouslyDisableSanitizersByTagID,
          googleTagManagerCustom: [ 'innerHTML' ],
          googleTagManagerNoscriptCustom: [ 'innerHTML' ],
        };
      }

      if (branding) {
        createVueMetaBranding.call(this, head, branding, { isAdminUI, nonce });
      }

      if (company.siteBranding && company.siteBranding.socialImage && company.siteBranding.socialImage.fileLocation) {
        head.meta.push({
          vmid: 'og:image',
          property: 'og:image',
          content: company.siteBranding.socialImage.fileLocation,
        });
        head.meta.push({
          vmid: 'twitter:image',
          name: 'twitter:image',
          content: company.siteBranding.socialImage.fileLocation,
        });
      }
    }

    head.script = (Array.isArray(head.script) ? head.script : []).concat({
      async: true,
      body: true,
      defer: true,
      src: 'https://amazon-cognito-assets.us-east-1.amazoncognito.com/amazon-cognito-advanced-security-data.min.js',
      // src: '/static/vendor/amazon-cognito-advanced-security-data.min.js',
      vmid: 'amazon-cognito-advanced-security-data',
      nonce,
    });

    if (process.env.VUE_ENV === 'server' && this.$ssrContext && this.$ssrContext.data) {
      const { apiHeaders: { Authorization: authHeader, 'X-Referrals-App': appHeader } = {} } = this.$ssrContext.data;
      if (authHeader && appHeader) {
        head.script = (Array.isArray(head.script) ? head.script : []).concat({
          vmid: 'appAuthToken',
          type: 'text/javascript',
          innerHTML: minifyCode(`
            (function () {
              window.IS_NATIVE_APP_BD93 = true;
              window.localStorage.setItem('token', '"${authHeader}"');
            })();
          `),
          nonce,
        });
        head.__dangerouslyDisableSanitizersByTagID = {
          ...head.__dangerouslyDisableSanitizersByTagID,
          appAuthToken: [ 'innerHTML' ],
        };
      }
    }

    // Client-side
    if (process.env.NODE_ENV === 'production' && process.env.VUE_ENV === 'client') {
      // If not admin-ui AND not native app
      if (!process.env.ADMIN_UI && !this.isNativeApp) {
        // Add hotjar support
        head.script = (Array.isArray(head.script) ? head.script : []).concat({
          vmid: 'hotjar',
          type: 'text/javascript',
          innerHTML: minifyCode(`
            (function (h,o,t,j,a,r) {
              h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};
              h._hjSettings={hjid:1207171,hjsv:6};
              a=o.getElementsByTagName('head')[0];
              r=o.createElement('script');r.async=1;
              r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;
              a.appendChild(r);
            })(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');
          `),
          body: true,
          nonce,
        });
        head.__dangerouslyDisableSanitizersByTagID = {
          ...head.__dangerouslyDisableSanitizersByTagID,
          hotjar: [ 'innerHTML' ],
        };
      }
    }

    return head;
  },
};
</script>

<style src="bootstrap/dist/css/bootstrap.css"></style>
<style src="bootstrap-vue/dist/bootstrap-vue.css"></style>
<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
<style src="simplemde/dist/simplemde.min.css"></style>
<style src="ag-grid-community/dist/styles/ag-grid.css"></style>
<style src="ag-grid-community/dist/styles/ag-theme-balham.css"></style>
<style src="vue2-daterange-picker/dist/vue2-daterange-picker.css"></style>

<style lang="scss">
@import './scss/main';
@import './scss/b-card-shadow';
@import './scss/b-row-cols';
@import './scss/dropzone.css';

:root {
  --safe-area-inset-top: 0px;
  --safe-area-inset-right: 0px;
  --safe-area-inset-bottom: 0px;
  --safe-area-inset-left: 0px;

  @supports (top: constant(safe-area-inset-top)) {
    --safe-area-inset-top: constant(safe-area-inset-top);
    --safe-area-inset-right: constant(safe-area-inset-right);
    --safe-area-inset-bottom: constant(safe-area-inset-bottom);
    --safe-area-inset-left: constant(safe-area-inset-left);
  }

  @supports (top: env(safe-area-inset-top)) {
    --safe-area-inset-top: env(safe-area-inset-top);
    --safe-area-inset-right: env(safe-area-inset-right);
    --safe-area-inset-bottom: env(safe-area-inset-bottom);
    --safe-area-inset-left: env(safe-area-inset-left);
  }
}

#app {
  padding-bottom: var(--safe-area-inset-bottom);
}
</style>



// WEBPACK FOOTER //
// src/App.vue